import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, X, Building2, Pencil, Check } from "lucide-react";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import EmptyState from "../../components/system/EmptyState";
import Input from "../../components/system/Input";

export default function StoresPage() {
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", code: "" });
  const [show, setShow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({ name: "", code: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get("/stores"), api.get("/users")]);
      setStores(s.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };
  useEffect(() => { load(); }, []);

  const userCountByStore = (storeId) =>
    users.filter((u) => u.store_id === storeId).length;

  const create = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/stores", form);
      toast.success("Store created");
      setShow(false);
      setForm({ name: "", code: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditingValues({ name: s.name, code: s.code || "" });
  };

  const saveEdit = async (id) => {
    if (!editingValues.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      await api.patch(`/stores/${id}`, editingValues);
      toast.success("Store updated");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete store "${s.name}"?`)) return;
    try {
      await api.delete(`/stores/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  return (
    <div data-testid="admin-stores-page">
      <PageHeader
        eyebrow="Locations"
        title="Stores"
        subtitle="Each user is linked to one store. All inventory and removals are scoped to that store."
        actions={
          <Button tier="primary" onClick={() => setShow(true)} data-testid="new-store-button">
            <Plus size={16} /> New store
          </Button>
        }
      />

      <Card variant="raised" className="overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_auto] gap-4 px-4 py-3 bg-paper-sunk border-b border-border/60 data-sku">
          <div>Name</div>
          <div>Code</div>
          <div>Users assigned</div>
          <div></div>
        </div>
        {stores.length === 0 ? (
          <div className="p-10 text-center font-display" data-testid="stores-empty">No stores yet.</div>
        ) : stores.map((s, idx) => {
          const isEditing = editingId === s.id;
          return (
            <div
              key={s.id}
              className={`md:grid grid-cols-[1.5fr_1fr_1fr_auto] gap-4 px-4 py-4 items-center ${idx > 0 ? "border-t border-border/40" : ""}`}
              data-testid={`store-row-${s.id}`}
            >
              <div className="flex items-center gap-3">
                <Building2 size={18} className="text-muted-foreground shrink-0" />
                {isEditing ? (
                  <Input
                    value={editingValues.name}
                    onChange={(e) => setEditingValues({ ...editingValues, name: e.target.value })}
                    className="flex-1 h-9"
                    data-testid={`edit-name-${s.id}`}
                  />
                ) : (
                  <div className="font-display font-semibold" data-testid={`store-name-${s.id}`}>{s.name}</div>
                )}
              </div>
              <div className="font-mono text-xs uppercase" data-testid={`store-code-${s.id}`}>
                {isEditing ? (
                  <Input
                    value={editingValues.code}
                    onChange={(e) => setEditingValues({ ...editingValues, code: e.target.value.toUpperCase() })}
                    placeholder="optional"
                    className="w-full max-w-[120px] h-9 uppercase font-mono"
                    data-testid={`edit-code-${s.id}`}
                  />
                ) : (
                  s.code || <span className="text-muted-foreground">—</span>
                )}
              </div>
              <div className="font-mono text-xs">
                <span className="text-ink font-bold" data-testid={`store-users-count-${s.id}`}>
                  {userCountByStore(s.id)}
                </span>{" "}
                <span className="text-muted-foreground">user(s)</span>
              </div>
              <div className="flex gap-2 justify-end mt-2 md:mt-0">
                {isEditing ? (
                  <>
                    <Button tier="secondary" size="sm" onClick={() => saveEdit(s.id)} data-testid={`save-store-${s.id}`}>
                      <Check size={12} /> Save
                    </Button>
                    <Button tier="tertiary" size="sm" onClick={() => setEditingId(null)} data-testid={`cancel-store-${s.id}`}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button tier="secondary" size="sm" onClick={() => startEdit(s)} data-testid={`edit-store-${s.id}`}>
                      <Pencil size={12} /> Edit
                    </Button>
                    <Button tier="secondary" size="sm" onClick={() => remove(s)} className="text-status-urgent" data-testid={`delete-store-${s.id}`}>
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </Card>

      {show && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 px-3" data-testid="new-store-dialog">
          <Card variant="raised" className="w-full sm:max-w-md p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="data-sku mb-1">Add a new store</div>
                <div className="font-display font-black text-2xl">New store</div>
              </div>
              <button onClick={() => setShow(false)} aria-label="Close" className="p-2 -mt-2 -mr-2 rounded-button text-ink-2 hover:text-ink"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="data-sku block mb-1">Name *</label>
                <Input
                  placeholder="e.g. Lyon Part-Dieu"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="new-store-name"
                  autoFocus
                />
              </div>
              <div>
                <label className="data-sku block mb-1">Code (optional)</label>
                <Input
                  placeholder="e.g. LYN-PD"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  className="uppercase font-mono"
                  data-testid="new-store-code"
                />
              </div>
              <Button tier="primary" className="w-full" onClick={create} disabled={submitting} data-testid="submit-new-store">
                {submitting ? "Creating…" : "Create store"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
